/**
 * Role Context Provider
 * 
 * Provides role-based access control context throughout the application.
 * Extracts user role from Cognito JWT token and provides helper functions
 * for permission checking.
 */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

// Role types
export type UserRole = 'Admin' | 'Production' | 'User';

// Role context interface
interface RoleContextType {
  role: UserRole;
  isLoading: boolean;
  isAdmin: () => boolean;
  isProduction: () => boolean;
  isStandardUser: () => boolean;
  hasPermission: (requiredRole: UserRole | UserRole[]) => boolean;
  refreshRole: () => Promise<void>;
}

// Create context
const RoleContext = createContext<RoleContextType | undefined>(undefined);

// Session storage key and TTL
const ROLE_CACHE_KEY = 'user_role_cache';
const ROLE_CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

interface RoleCache {
  role: UserRole;
  timestamp: number;
}

/**
 * Extract role from Cognito JWT token
 */
const extractRoleFromToken = async (): Promise<UserRole> => {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken;
    
    if (!idToken) {
      console.warn('No ID token found, defaulting to User role');
      return 'User';
    }

    // Extract custom:role claim from token payload
    const payload = idToken.payload;
    const customRole = payload['custom:role'] as string | undefined;
    
    // Validate role value
    if (customRole === 'Admin' || customRole === 'Production' || customRole === 'User') {
      return customRole;
    }
    
    // Default to User if role is missing or invalid
    console.warn(`Invalid or missing role in token: ${customRole}, defaulting to User`);
    return 'User';
    
  } catch (error) {
    console.error('Error extracting role from token:', error);
    return 'User';
  }
};

/**
 * Get cached role from session storage
 */
const getCachedRole = (): UserRole | null => {
  try {
    const cached = sessionStorage.getItem(ROLE_CACHE_KEY);
    if (!cached) return null;
    
    const roleCache: RoleCache = JSON.parse(cached);
    const now = Date.now();
    
    // Check if cache is still valid (within TTL)
    if (now - roleCache.timestamp < ROLE_CACHE_TTL) {
      return roleCache.role;
    }
    
    // Cache expired, remove it
    sessionStorage.removeItem(ROLE_CACHE_KEY);
    return null;
    
  } catch (error) {
    console.error('Error reading role cache:', error);
    return null;
  }
};

/**
 * Cache role in session storage
 */
const cacheRole = (role: UserRole): void => {
  try {
    const roleCache: RoleCache = {
      role,
      timestamp: Date.now()
    };
    sessionStorage.setItem(ROLE_CACHE_KEY, JSON.stringify(roleCache));
  } catch (error) {
    console.error('Error caching role:', error);
  }
};

/**
 * Role Provider Component
 */
interface RoleProviderProps {
  children: ReactNode;
}

export const RoleProvider: React.FC<RoleProviderProps> = ({ children }) => {
  const [role, setRole] = useState<UserRole>('User');
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Load role from token (always reads fresh from JWT)
   */
  const loadRole = async () => {
    setIsLoading(true);
    
    try {
      // Always extract from token to avoid stale cache issues
      const extractedRole = await extractRoleFromToken();
      console.log(`[RoleContext] Role extracted from token: ${extractedRole}`);
      setRole(extractedRole);
      cacheRole(extractedRole);
      
    } catch (error) {
      console.error('Error loading role:', error);
      setRole('User'); // Default to User on error
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Refresh role from token (bypass cache)
   */
  const refreshRole = async () => {
    try {
      // Clear cache
      sessionStorage.removeItem(ROLE_CACHE_KEY);
      
      // Extract fresh role from token
      const extractedRole = await extractRoleFromToken();
      setRole(extractedRole);
      cacheRole(extractedRole);
      
    } catch (error) {
      console.error('Error refreshing role:', error);
    }
  };

  /**
   * Check if user is Admin
   */
  const isAdmin = (): boolean => {
    return role === 'Admin';
  };

  /**
   * Check if user is Production
   */
  const isProduction = (): boolean => {
    return role === 'Production';
  };

  /**
   * Check if user is Standard User
   */
  const isStandardUser = (): boolean => {
    return role === 'User';
  };

  /**
   * Check if user has required permission
   * @param requiredRole - Single role or array of roles
   */
  const hasPermission = (requiredRole: UserRole | UserRole[]): boolean => {
    if (Array.isArray(requiredRole)) {
      return requiredRole.includes(role);
    }
    return role === requiredRole;
  };

  // Load role on mount
  useEffect(() => {
    loadRole();
  }, []);

  // Refresh role on window focus (user might have changed role in another tab)
  useEffect(() => {
    const handleFocus = () => {
      // Check if cache is expired
      const cachedRole = getCachedRole();
      if (!cachedRole) {
        // Cache expired, refresh role
        refreshRole();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const contextValue: RoleContextType = {
    role,
    isLoading,
    isAdmin,
    isProduction,
    isStandardUser,
    hasPermission,
    refreshRole
  };

  return (
    <RoleContext.Provider value={contextValue}>
      {children}
    </RoleContext.Provider>
  );
};

/**
 * Hook to use Role Context
 */
export const useRole = (): RoleContextType => {
  const context = useContext(RoleContext);
  
  if (context === undefined) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  
  return context;
};

/**
 * Hook to check if user has specific permission
 * @param requiredRole - Single role or array of roles
 */
export const useHasPermission = (requiredRole: UserRole | UserRole[]): boolean => {
  const { hasPermission } = useRole();
  return hasPermission(requiredRole);
};

/**
 * Hook to check if user is Admin
 */
export const useIsAdmin = (): boolean => {
  const { isAdmin } = useRole();
  return isAdmin();
};

/**
 * Hook to check if user is Production
 */
export const useIsProduction = (): boolean => {
  const { isProduction } = useRole();
  return isProduction();
};

/**
 * Hook to check if user is Standard User
 */
export const useIsStandardUser = (): boolean => {
  const { isStandardUser } = useRole();
  return isStandardUser();
};
